import { fetcher } from '../../../lib/api-fetcher';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApprovalItem, ApprovalListResponse, ApprovalType } from '../types.js';



export const approvalsHooks = {
  useList: ({
    page,
    limit,
    type,
    orgUnitId,
  }: {
    page: number;
    limit: number;
    type?: string;
    orgUnitId?: string;
  }) => {
    return useQuery<ApprovalListResponse>({
      queryKey: ['approvals', 'list', page, limit, type, orgUnitId],
      queryFn: async () => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        if (type) params.append('type', type);
        if (orgUnitId) params.append('orgUnitId', orgUnitId);

        // Fetch using fetcher (or direct fetch with headers)
        // Since createFetcher handles auth headers automatically, we use fetcher.get
        const res = await fetcher.get<ApprovalListResponse>(`/staff/v1/approvals?${params.toString()}`);
        return res;
      },
    });
  },

  useDetail: (type: ApprovalType, id: string) => {
    return useQuery<ApprovalItem>({
      queryKey: ['approvals', 'detail', type, id],
      queryFn: async () => {
        const res = await fetcher.get<ApprovalItem>(`/staff/v1/approvals/${type}/${id}`);
        return res;
      },
      enabled: !!type && !!id,
    });
  },

  useApprove: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async ({
        type,
        id,
        registrarRef,
      }: {
        type: ApprovalType;
        id: string;
        registrarRef?: string;
      }) => {
        const res = await fetcher.post<unknown>(`/staff/v1/approvals/${type}/${id}/approve`, {
          registrarRef,
        });
        return res;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['approvals', 'list'] });
      },
    });
  },

  useReject: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async ({
        type,
        id,
        reason,
      }: {
        type: ApprovalType;
        id: string;
        reason: string;
      }) => {
        const res = await fetcher.post<unknown>(`/staff/v1/approvals/${type}/${id}/reject`, {
          reason,
        });
        return res;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['approvals', 'list'] });
      },
    });
  },

  useCancel: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async ({
        type,
        id,
      }: {
        type?: ApprovalType;
        id: string;
      }) => {
        const url = type ? `/staff/v1/approvals/${type}/${id}/cancel` : `/staff/v1/approvals/${id}/cancel`;
        const res = await fetcher.post<unknown>(url, {});
        return res;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['approvals', 'list'] });
      },
    });
  },
};

